/**
 * PURPOSE: Estado de los participantes del Centro de Programación (FTR-0028 WG3, §17; D-36).
 * RESPONSIBILITIES: Mostrar qué slots están resueltos, cuáles siguen pendientes y cuáles son
 *   ambiguos; sincronizar desde el sorteo y asignar una institución a un slot.
 *
 *   EL SORTEO ES LA AUTORIDAD (D-36). El cliente NUNCA manda `idparticipant1`/`idparticipant2` a un
 *   partido. Manda, como mucho, qué institución va en qué slot; el backend lee `participants` y
 *   escribe los partidos. Dejar que la interfaz asignara participantes crearía una segunda verdad
 *   que se contradiría con el sorteo en cuanto alguien lo cambiase.
 *
 *   UN SLOT AMBIGUO NO SE ADIVINA. Si dos instituciones activas comparten `(idgrupo, nro_grupo)`, el
 *   backend deja esos partidos sin resolver y lo reporta. Aquí se muestra tal cual: elegir una por
 *   orden de llegada sería inventar el sorteo.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/ParticipantsPanel.jsx
 */
import React, { useMemo, useState } from "react";
import { FiAlertTriangle, FiRefreshCw, FiUserCheck } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { groupLetter, participantLabel } from "../../../../utils/programmingCenterModel.js";
import SidePanel from "../components/SidePanel.jsx";

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

const ParticipantsPanel = ({ open, onClose }) => {
  const { context, draft, actions, busy, summary } = useProgrammingCenter();
  const [assignment, setAssignment] = useState({ idparticipant: "", idgrupo: "", nro_grupo: "" });
  const [resolution, setResolution] = useState(null);

  const participants = useMemo(() => context?.participants || [], [context]);
  const groupSizes = useMemo(() => draft?.draft?.group_sizes || [], [draft]);

  const { assigned, unassigned } = useMemo(() => {
    const withSlot = participants.filter((item) => item.idgrupo && item.nro_grupo);
    return {
      assigned: withSlot,
      unassigned: participants.filter((item) => !item.idgrupo || !item.nro_grupo),
    };
  }, [participants]);

  const slots = useMemo(() => {
    const list = [];
    groupSizes.forEach((size, position) => {
      for (let slot = 1; slot <= Number(size); slot += 1) {
        const group = position + 1;
        const holder = assigned.find(
          (item) => Number(item.idgrupo) === group && Number(item.nro_grupo) === slot
        );
        list.push({
          group,
          slot,
          label: `${groupLetter(group)}${slot}`,
          holder: holder || null,
        });
      }
    });
    return list;
  }, [groupSizes, assigned]);

  const runResolve = async (clearMissing) => {
    const result = await actions.resolveParticipants(clearMissing);
    if (result.ok) {
      setResolution(result.data);
      toast.success(
        `${result.data.resolved} partidos resueltos · ${result.data.pending} lados pendientes`
      );
    }
  };

  const runAssign = async () => {
    const result = await actions.assignSlot({
      idparticipant: Number(assignment.idparticipant),
      idgrupo: Number(assignment.idgrupo),
      nro_grupo: Number(assignment.nro_grupo),
    });
    if (result.ok) {
      toast.success("Institución asignada al slot y partidos resueltos.");
      setAssignment({ idparticipant: "", idgrupo: "", nro_grupo: "" });
    }
  };

  const canAssign =
    Number(assignment.idparticipant) > 0 &&
    Number(assignment.idgrupo) > 0 &&
    Number(assignment.nro_grupo) > 0;

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Participantes y slots"
      subtitle={`${assigned.length} de ${participants.length} instituciones sorteadas`}
    >
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => runResolve(false)}
          disabled={Boolean(busy)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:opacity-50"
        >
          <FiRefreshCw className="text-sm" />
          Sincronizar participantes
        </button>
        <button
          type="button"
          onClick={() => {
            if (
              window.confirm(
                "Vaciar los participantes de los partidos cuyo slot ya no tiene institución. ¿Continuar?"
              )
            ) {
              runResolve(true);
            }
          }}
          disabled={Boolean(busy)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-600 transition hover:border-amber-300 hover:text-amber-700 disabled:opacity-50"
        >
          Sincronizar y limpiar faltantes
        </button>
      </div>

      {summary.participantsPending ? (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800">
          {summary.participantsPending} partidos materializados siguen sin participantes. Antes del
          sorteo es lo esperado: el partido ya existe con su horario y su sede.
        </p>
      ) : null}

      {resolution?.ambiguousSlots?.length ? (
        <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3">
          <p className="flex items-center gap-2 text-sm font-black text-rose-800">
            <FiAlertTriangle className="text-sm" />
            {resolution.ambiguousSlots.length} slots con más de una institución
          </p>
          <p className="mt-1 text-xs font-bold text-rose-800">
            Esos partidos quedan sin resolver hasta que el sorteo tenga una sola institución por
            slot. El Centro no elige por ti.
          </p>
          <p className="mt-2 text-xs font-black text-rose-900">
            {resolution.ambiguousSlots
              .map((slot) => `${groupLetter(slot.idgrupo)}${slot.nro_grupo}`)
              .join(" · ")}
          </p>
        </div>
      ) : null}

      {resolution?.blockedMatches?.length ? (
        <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-bold text-slate-600">
          {resolution.blockedMatches.length} partidos no se re-resolvieron porque ya tienen actividad
          competitiva.
        </p>
      ) : null}

      <section className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
          <FiUserCheck className="text-xs" />
          Asignar institución a un slot
        </p>
        <div className="grid gap-2 sm:grid-cols-3">
          <select
            value={assignment.idparticipant}
            onChange={(event) =>
              setAssignment((current) => ({ ...current, idparticipant: event.target.value }))
            }
            className={inputClass}
            aria-label="Institución"
          >
            <option value="">Institución…</option>
            {participants.map((participant) => (
              <option key={participant.idparticipant} value={participant.idparticipant}>
                {participantLabel(participant) || `Participante ${participant.idparticipant}`}
              </option>
            ))}
          </select>
          <select
            value={assignment.idgrupo}
            onChange={(event) =>
              setAssignment((current) => ({ ...current, idgrupo: event.target.value }))
            }
            className={inputClass}
            aria-label="Grupo"
          >
            <option value="">Grupo…</option>
            {groupSizes.map((unused, position) => (
              <option key={position + 1} value={position + 1}>
                Grupo {groupLetter(position + 1)}
              </option>
            ))}
          </select>
          <select
            value={assignment.nro_grupo}
            onChange={(event) =>
              setAssignment((current) => ({ ...current, nro_grupo: event.target.value }))
            }
            className={inputClass}
            aria-label="Slot"
          >
            <option value="">Slot…</option>
            {Array.from(
              { length: Number(groupSizes[Number(assignment.idgrupo) - 1]) || 0 },
              (unused, position) => position + 1
            ).map((slot) => (
              <option key={slot} value={slot}>
                Slot {slot}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={runAssign}
          disabled={!canAssign || Boolean(busy)}
          className="mt-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:opacity-50"
        >
          Asignar y resolver
        </button>
        <p className="mt-2 text-[11px] font-bold text-slate-500">
          Esto escribe la autoridad del sorteo (grupo y slot de la institución); los participantes de
          cada partido los escribe el servidor a continuación.
        </p>
      </section>

      <section className="mt-4">
        <p className="mb-2 text-[11px] font-black uppercase tracking-wide text-slate-500">
          Slots del fixture
        </p>
        {slots.length ? (
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
            {slots.map((slot) => (
              <div
                key={slot.label}
                className={`rounded-lg border px-2 py-1.5 ${
                  slot.holder
                    ? "border-emerald-200 bg-emerald-50"
                    : "border-amber-200 bg-amber-50"
                }`}
              >
                <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">
                  {slot.label}
                </p>
                <p className="truncate text-xs font-black text-slate-800">
                  {slot.holder ? participantLabel(slot.holder) : "A definir"}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs font-bold text-slate-500">
            Todavía no hay una distribución de grupos que mostrar.
          </p>
        )}
        {unassigned.length ? (
          <p className="mt-2 text-[11px] font-bold text-slate-500">
            {unassigned.length} instituciones inscritas aún sin grupo ni slot.
          </p>
        ) : null}
      </section>
    </SidePanel>
  );
};

export default ParticipantsPanel;
